import { IComponentOptions, IRootScopeService, IScope } from "angular";
import { HierarchyNode } from "d3-hierarchy";
import { GtmhubController } from "@gtmhub/core";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { ProfileScreenName, ProfileType } from "@gtmhub/profile";
import { createSessionTree } from "@gtmhub/sessions/controllers/utils";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { getSessionDurationInDays, isActiveSession } from "@gtmhub/sessions/redux/session-selectors";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { checkOverflow } from "@gtmhub/shared/utils/tooltips-util";
import { INgRedux } from "@gtmhub/state-management";
import { byDateFieldDesc, byDaysDuration, byEach } from "@gtmhub/util/sorting-utils";
import { ISessionsIdMap, Session } from "@webapp/sessions/models/sessions.model";

export type ProfileFilterChangedEventArgs = {
  selectedFilter: string;
};
export class ProfileSessionFilterEvents {
  private $rootScope: IRootScopeService;

  constructor(private $scope: IScope) {
    this.$rootScope = this.$scope.$root;
  }

  broadcastFilterChanged(selectedFilter: string): void {
    this.$rootScope.$broadcast("ProfileSessionFilterChanged", { selectedFilter });
  }

  onFilterChanged(callback: (args: ProfileFilterChangedEventArgs) => unknown): void {
    this.$scope.$on("ProfileSessionFilterChanged", (evt, args: ProfileFilterChangedEventArgs) => callback(args));
  }
}

type ISessionGroup = {
  label: string;
  sessionsTree?: HierarchyNode<Session>;
  expanded: boolean;
  noSessionsInfoLabel?: string;
};

interface IProfileSessionSelectorRedux {
  sessionIdMap: ISessionsIdMap;
  sessionsFetched: boolean;
  sessionsError: IUIError;
}

interface IProfileSessionSelectorBindings {
  sessions: Session[];
  targetType: ProfileType;
}

const bindStateToThis = (state: ISessionsStoreState): IProfileSessionSelectorRedux => ({
  sessionIdMap: state.sessions.map,
  sessionsError: state.sessions.error,
  sessionsFetched: state.sessions.isFetched,
});

export interface ProfileSessionFilterCtrl extends IProfileSessionSelectorRedux, IProfileSessionSelectorBindings {}
export class ProfileSessionFilterCtrl extends GtmhubController {
  selectedSessionId: string;
  sessionGroups: ISessionGroup[];
  screenName: ProfileScreenName;

  private profileSessionFilterEvents: ProfileSessionFilterEvents;

  static $inject = ["$ngRedux", "PlanningSessionsActions", "$rootScope", "$scope", "UIErrorHandlingService"];

  constructor(
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private $rootScope: ITraceRootScopeService,
    private $scope: IExtendedRootScopeService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    super();
    this.profileSessionFilterEvents = new ProfileSessionFilterEvents(this.$scope);
  }

  $onInit(): void {
    this.onDestroy(this.$ngRedux.connect(bindStateToThis)(this));
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

    this.screenName = this.targetType === "user" ? "employee profile" : "team profile";

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.sessionsFetched && !!this.sessions && !!this.sessions.length,
        () => this.sessionsError
      )
      .then(
        () => {
          this.sessionGroups = this.getSessionGroups(angular.copy(this.sessions));
          this.selectedSessionId = this.getFirstSessionIdFromFirstGroupWithSessions(this.sessionGroups);
          this.broadcastChanges(this.selectedSessionId);
        },
        () => this.uiErrorHandlingService.handleModal(this.sessionsError)
      );
  }

  onSelect(id: string): void {
    this.$rootScope.traceAction("change_filter", () => {
      if (id && id !== this.selectedSessionId) {
        this.selectedSessionId = id;

        this.broadcastChanges(this.selectedSessionId);
      }
    });
  }

  checkOverflow(id: string): boolean {
    return checkOverflow(id);
  }

  getSessionDurationInDays(session: Session): number {
    return getSessionDurationInDays(session);
  }

  private broadcastChanges(selectedFilterValue: string) {
    this.profileSessionFilterEvents.broadcastFilterChanged(selectedFilterValue);
  }

  private getSessionGroups = (sessions: Session[]): ISessionGroup[] => {
    const sessionsGroups: ISessionGroup[] = [];

    const activeSessions = [];
    const otherSessions = [];

    sessions.sort(byEach(byDateFieldDesc("start"), byDaysDuration("start", "end")));
    sessions.forEach((session) => {
      if (isActiveSession(session)) {
        activeSessions.push(session);
      } else {
        otherSessions.push(session);
      }
    });

    [
      { label: "active_sessions", sessions: activeSessions, noSessionsInfoLabel: "no_OKRs_in_active_sessions" },
      { label: "other_sessions", sessions: otherSessions },
    ].forEach(({ label, sessions, noSessionsInfoLabel }) => {
      if (sessions.length) {
        const sessionsTree = createSessionTree(sessions);
        const sessionsGroup: ISessionGroup = { label: label, sessionsTree, expanded: true };
        sessionsGroups.push(sessionsGroup);
      } else if (noSessionsInfoLabel) {
        const sessionsGroup: ISessionGroup = { label: label, expanded: true, noSessionsInfoLabel };
        sessionsGroups.push(sessionsGroup);
      }
    });

    return sessionsGroups;
  };

  private getFirstSessionIdFromFirstGroupWithSessions(sessionsGroups: ISessionGroup[]): string {
    for (const sessionGroup of sessionsGroups) {
      if (sessionGroup.sessionsTree) {
        return sessionGroup.sessionsTree.children[0].data.id;
      }
    }
  }
}

export const ProfileSessionFilterComponent: IComponentOptions = {
  bindings: {
    sessions: "=",
    targetType: "<",
  },
  controller: ProfileSessionFilterCtrl,
  template: require("./profile-session-filter.html"),
};
